/**
 * CampaignTemplateCard — card de template na galeria /campaigns/templates.
 *
 * Memoizado para evitar re-renders no grid quando só o filtro muda.
 * Botão "Usar este template" chama o clone mutation e navega pro editor
 * ao sucesso via callback `onUsed(campaignId)`.
 *
 * TH-55142 (M-campT.4)
 */

import { memo } from "react";
import { FileText } from "lucide-react";

import { Badge } from "@/components/ui/Badge";
import { Button } from "@/components/ui/Button";
import { type CampaignTemplateRead } from "@/hooks/useCampaignTemplates";
import { cn } from "@/lib/utils";

// ---------------------------------------------------------------------------
// Metadados de categoria — espelha CATEGORY_LABEL de NewCampaignDialog
// ---------------------------------------------------------------------------

const CATEGORY_META: Record<
  string,
  { label: string; badgeVariant: "success" | "warn" | "destructive" | "muted" }
> = {
  // Vocabulário do backend (`^(classica|promocional)$`).
  classica: { label: "Clássica", badgeVariant: "muted" },
  promocional: { label: "Promocional", badgeVariant: "warn" },
  // Labels legados de departamento — mantidos para retrocompat com
  // possíveis mocks/dados antigos durante a transição.
  hortifruti: { label: "Hortifrúti", badgeVariant: "success" },
  acougue: { label: "Açougue", badgeVariant: "destructive" },
  padaria: { label: "Padaria", badgeVariant: "warn" },
  mercearia: { label: "Mercearia", badgeVariant: "muted" },
  bebidas: { label: "Bebidas", badgeVariant: "success" },
  promo: { label: "Promo", badgeVariant: "warn" },
};

const FAMILY_LABEL: Record<string, string> = {
  clean: "Clean",
  cartazista: "Cartazista",
};

function categoryMeta(category: string) {
  return (
    CATEGORY_META[category] ?? {
      label: category,
      badgeVariant: "muted" as const,
    }
  );
}

// ---------------------------------------------------------------------------
// Props
// ---------------------------------------------------------------------------

export interface CampaignTemplateCardProps {
  template: CampaignTemplateRead;
  /** True enquanto ESTE card está no meio do clone (outro card pode estar disabled mas não loading). */
  isCloning: boolean;
  /** True quando qualquer clone está em andamento (desabilita todos os botões). */
  anyCloning: boolean;
  onUse: (template: CampaignTemplateRead) => void;
}

// ---------------------------------------------------------------------------
// Componente
// ---------------------------------------------------------------------------

export const CampaignTemplateCard = memo(function CampaignTemplateCard({
  template,
  isCloning,
  anyCloning,
  onUse,
}: CampaignTemplateCardProps) {
  const meta = categoryMeta(template.category);

  return (
    <article
      className={cn(
        "flex flex-col overflow-hidden rounded-lg border border-[var(--color-border)]",
        "bg-[var(--color-card)] transition-colors hover:border-[var(--color-border-strong)]",
      )}
      data-testid={`template-card-${template.id}`}
    >
      {/* Preview */}
      <div className="flex aspect-video items-center justify-center overflow-hidden bg-[var(--color-secondary)]">
        {template.thumb_url ? (
          <img
            src={template.thumb_url}
            alt={`Pré-visualização do template ${template.name}`}
            className="h-full w-full object-cover"
            loading="lazy"
          />
        ) : (
          <FileText
            className="h-10 w-10 text-[var(--color-muted-foreground)]"
            aria-hidden="true"
          />
        )}
      </div>

      {/* Body */}
      <div className="flex flex-1 flex-col gap-2 p-4">
        <div className="flex items-start justify-between gap-2">
          <h3 className="text-sm font-semibold leading-tight text-[var(--color-foreground)]">
            {template.name}
          </h3>
          <div className="flex shrink-0 flex-wrap items-center justify-end gap-1">
            <Badge variant={meta.badgeVariant}>{meta.label}</Badge>
            {template.family && FAMILY_LABEL[template.family] && (
              <Badge
                variant="muted"
                className="text-[10px]"
                data-testid={`template-family-${template.id}`}
              >
                {FAMILY_LABEL[template.family]}
              </Badge>
            )}
          </div>
        </div>

        {template.description ? (
          <p
            className="line-clamp-2 text-xs text-[var(--color-muted-foreground)]"
            title={template.description}
          >
            {template.description}
          </p>
        ) : null}

        {/* Pushes o botão para baixo quando a descrição é curta */}
        <div className="mt-auto pt-2">
          <Button
            type="button"
            variant="outline"
            size="sm"
            className="w-full"
            disabled={anyCloning}
            onClick={() => onUse(template)}
            aria-label={`Usar template ${template.name}`}
            data-testid={`use-template-${template.id}`}
          >
            {isCloning ? "Criando campanha…" : "Usar este template"}
          </Button>
        </div>
      </div>
    </article>
  );
});
